import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from './apiConfig';
import { Button } from './components/ui';
import AuthLayout, { AuthAlert, AuthField, AuthMessage, readJson } from './components/auth/AuthLayout';

/** The verification token is a JWT carrying the address it was issued for. */
function emailFromToken(token) {
    try {
        const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const email = JSON.parse(atob(part.padEnd(part.length + ((4 - (part.length % 4)) % 4), '='))).email;
        return typeof email === 'string' ? email : '';
    } catch {
        return '';
    }
}

/**
 * What the portal said about a verify attempt. POST /auth/verify-email/verify {token, password}:
 * 200 = verified (the body carries an auth token), 400 'Wrong password' = not the password the
 * address was signed up with, 400 'Expired Token' / 'Invalid Token' = the link is spent.
 */
function verifyProblem(status, data) {
    const message = data.message || data.error || '';
    if (/wrong password/i.test(message)) return 'wrong-password';
    if (status === 429 || /too many/i.test(message)) return 'too-many';
    if (/token/i.test(message)) return 'failed';
    return 'error';
}

const WRONG_PASSWORD = {
    title: 'That is not the password this address was signed up with.',
    text: 'If you did not sign up again yourself, someone else may have used your address: sign up again with your own password and use the new email.',
};

/**
 * /verify-email/verify/:token — the link from the verification email. It no longer verifies on
 * load: the person types the password they chose when they signed up, so a link resent to an
 * address someone else re-registered cannot verify that other person's account (security audit
 * 2026-10-18, H1). Then: verified | link not valid | stays on the form with what went wrong.
 */
function EmailVerifiedPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState(token ? 'form' : 'failed');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [tried, setTried] = useState(false);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState(null);
    const pwRef = useRef(null);

    const email = token ? emailFromToken(token) : '';
    const pwErr = !password ? 'Enter your password.' : '';

    async function verify(e) {
        e.preventDefault();
        if (busy) return;
        if (pwErr) {
            setTried(true);
            pwRef.current?.focus();
            return;
        }
        setProblem(null);
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/auth/verify-email/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password }),
            });
            if (res.ok) {
                setStatus('verified');
                return;
            }
            const p = verifyProblem(res.status, await readJson(res));
            if (p === 'failed') {
                setStatus('failed');
                return;
            }
            setProblem(p);
            if (p === 'wrong-password') pwRef.current?.focus();
        } catch {
            setProblem('offline');
        } finally {
            setBusy(false);
        }
    }

    const toLogin = <Link className="au-link" to="/login">Back to log in</Link>;

    let screen;
    if (status === 'verified') {
        screen = (
            <AuthMessage
                tone="success"
                icon="check"
                kicker="Verified"
                title="Your email is verified"
                links={null}
            >
                <Button variant="primary" size="lg" block to="/login">Continue to log in</Button>
            </AuthMessage>
        );
    } else if (status === 'failed') {
        screen = (
            <AuthMessage
                tone="danger"
                icon="cross"
                kicker="Link not valid"
                title="This link has expired or was already used"
                links={toLogin}
            >
                <Button
                    variant="primary"
                    size="lg"
                    block
                    onClick={() => navigate('/verify-email', { state: email ? { email, from: 'link' } : { from: 'link' } })}
                >
                    Send a new link
                </Button>
            </AuthMessage>
        );
    } else {
        let alert = null;
        if (problem === 'wrong-password') {
            alert = (
                <AuthAlert title={WRONG_PASSWORD.title} action={<Link className="au-link" to="/sign-up">Sign up again</Link>}>
                    {WRONG_PASSWORD.text}
                </AuthAlert>
            );
        } else if (problem === 'too-many') {
            alert = <AuthAlert title="Too many attempts.">Wait a few minutes, then try again.</AuthAlert>;
        } else if (problem === 'offline') {
            alert = <AuthAlert title="We couldn’t reach enAble.">Check your connection and try again.</AuthAlert>;
        } else if (problem === 'error') {
            alert = <AuthAlert title="We couldn’t verify your email." />;
        }
        screen = (
            <AuthMessage
                tone="accent"
                icon="mail"
                kicker="Verify your email"
                title="Enter your password"
                body={`The one you chose when you signed up${email ? ` as ${email}` : ''}`}
                links={toLogin}
            >
                {alert}
                <form className="au-form" onSubmit={verify} noValidate>
                    {/* lets a password manager match the saved sign-in for this address */}
                    {email ? <input type="email" name="email" autoComplete="username" value={email} readOnly hidden /> : null}
                    <AuthField
                        label="Password"
                        type={showPw ? 'text' : 'password'}
                        name="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            if (problem) setProblem(null);
                        }}
                        error={tried ? pwErr : ''}
                        inputRef={pwRef}
                        trailing={(
                            <button
                                type="button"
                                className="au-link au-link--sm"
                                aria-label={showPw ? 'Hide password' : 'Show password'}
                                aria-pressed={showPw}
                                onClick={() => setShowPw((v) => !v)}
                            >
                                {showPw ? 'Hide' : 'Show'}
                            </button>
                        )}
                    />
                    <Button type="submit" variant="primary" size="lg" block aria-disabled={busy || undefined}>
                        {busy ? 'Verifying…' : 'Verify'}
                    </Button>
                </form>
            </AuthMessage>
        );
    }

    return <AuthLayout>{screen}</AuthLayout>;
}

export default EmailVerifiedPage;
